import { ColumnsType } from 'antd/es/table/interface';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import {
	StopOutlined,
	KeyOutlined,
	PlusOutlined,
	EditFilled,
	CaretUpOutlined,
	CaretDownOutlined,
} from '@ant-design/icons';

import { TableColumnType, Tooltip } from 'antd';
import './countryAndPaymMethodColumn.scss';

import { AdminUser } from '../models/AdminUsers';
import { handleSortClick } from './sorterColumn';
import { ColumnTitle } from '../components/ColumnTitle/ColumnTitle';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '../constant/dateFormat';
import { useConfigurationContext } from '../store/configurationContext';
import { getUserInfo } from './localStorageHandler';

const parseAllowedAdminEmails = (raw: string | undefined) =>
	(raw ?? '')
		.split(',')
		.map((e) => e.trim().toLowerCase())
		.filter(Boolean);

export const useGetAdminUsersColumn = (
	t: TFunction<'translation', undefined, 'translation'>,
	sortBy: string,
	setSortBy: (sortValue: string) => void,
	setUser: (user: AdminUser) => void,
	setShowModal: (value: boolean) => void,
	role: string,
	isMobile: boolean,
) => {
	const { configurationState } = useConfigurationContext();
	const allowedAdminsRaw = configurationState.find((c) => c.configKey === 'allowed_admins_emails')?.value;
	const allowedAdminEmails = parseAllowedAdminEmails(allowedAdminsRaw);
	const currentAdminEmail = (getUserInfo()?.email ?? '').trim().toLowerCase();
	const isSpecialSuperUser =
		role === 'SUPER_USER' && Boolean(currentAdminEmail) && allowedAdminEmails.includes(currentAdminEmail);
	const columns: ColumnsType<AdminUser> = [
		{
			responsive: ['lg'],
			title: (
				<div>
					<Link to={`/add-admin`}>
						<PlusOutlined className='flex items-center cursor-pointer  text-black font-bold' alt='add' />
					</Link>
				</div>
			),
			dataIndex: 'add',
			key: 'add',
			width: '7%',
			ellipsis: true,
			render: (value, record) => {
				return (
					<div className='flex items-center w-[100%]'>
						{record.role === 'ADMIN' && role === 'SUPER_USER' && (
							<Tooltip placement='top' title={t<string>('passwordChange')}>
								<Link to={`/change-password?userAdmin=${record.id}`}>
									<KeyOutlined className=' text-black' />
								</Link>
							</Tooltip>
						)}
						<Link to={`/add-admin?userAdmin=${record.id}`}>
							<EditFilled className='flex items-center cursor-pointer  text-black font-bold' />
						</Link>
						{isSpecialSuperUser && record.role === 'ADMIN' && (
							<Tooltip placement='top' title={record.userStatus === 'ACTIVE' ? 'De-Activate' : 'Activate'}>
								<StopOutlined
									className='mx-2'
									onClick={() => {
										setShowModal(true);
										setUser(record);
									}}
								/>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			responsive: ['lg'],
			width: '10%',
			title: (
				<div className='flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('name')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'name-asc'))}
							style={{ color: sortBy === 'name-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'name-desc'))}
							style={{ color: sortBy === 'name-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			render: (value, record) => {
				return (
					<div className='word-break-column  text-[#00142A] flex items-center font-medium'>{`${record.firstname} ${record.lastname}`}</div>
				);
			},
		},
		{
			responsive: ['lg'],
			width: '12%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('emailAddress')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'email-asc'))}
							style={{ color: sortBy === 'email-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'email-desc'))}
							style={{ color: sortBy === 'email-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'email-address',
			key: 'email-address',
			render: (value, record) => {
				return <div className='word-break-column  text-[#626A74] flex items-center font-medium'>{record.email}</div>;
			},
		},
		{
			responsive: ['lg'],
			width: '7%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('countryCode')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'countryCode-asc'))}
							style={{ color: sortBy === 'countryCode-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'countryCode-desc'))}
							style={{ color: sortBy === 'countryCode-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'country-code',
			key: 'country-code',
			render: (value, record) => (
				<div className='text-[#626A74] flex items-center  font-medium'>{record.countryCode}</div>
			),
		},
		{
			responsive: ['lg'],
			width: '10%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('phoneNumber')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'phone-asc'))}
							style={{ color: sortBy === 'phone-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'phone-desc'))}
							style={{ color: sortBy === 'phone-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'phone',
			key: 'phone',
			render: (value, record) => {
				return <div className='font-medium text-[#000D1D99] flex items-center'>{record.phone}</div>;
			},
		},
		{
			responsive: ['lg'],
			width: '10%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('role')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'role-asc'))}
							style={{ color: sortBy === 'role-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'role-desc'))}
							style={{ color: sortBy === 'role-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'role',
			key: 'role',
			render: (value, record) => {
				return <div className=' font-medium text-[#000D1D99] flex items-center'>{record.role}</div>;
			},
		},
		{
			responsive: ['lg'],
			width: '10%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('creationDate')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'createdAt-asc'))}
							style={{ color: sortBy === 'createdAt-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'createdAt-desc'))}
							style={{ color: sortBy === 'createdAt-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (value, record) => {
				return (
					<div className=' font-medium text-[#000D1D99] flex items-center'>
						{dayjs(record.createdAt).format(DATE_TIME_FORMAT)}
					</div>
				);
			},
		},
		{
			responsive: ['lg'],
			width: '6%',
			title: (
				<div className=' flex items-center cursor-pointer'>
					<ColumnTitle title={t<string>('status')} />
					<div className='flex flex-col mx-2 text-xs mt-1'>
						<CaretUpOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'userStatus-asc'))}
							style={{ color: sortBy === 'userStatus-asc' ? 'black' : 'gray' }}
						/>
						<CaretDownOutlined
							onClick={() => setSortBy(handleSortClick(sortBy, 'userStatus-desc'))}
							style={{ color: sortBy === 'userStatus-desc' ? 'black' : 'gray' }}
							className='-mt-[3px]'
						/>
					</div>
				</div>
			),
			dataIndex: 'status',
			key: 'status',
			render: (value, record) => {
				return (
					<div
						className='font-medium text-[#000D1D99] flex items-center'
						style={{ color: record.userStatus === 'ACTIVE' ? 'green' : 'red' }}
					>
						{t(record.userStatus.toLocaleLowerCase())}
					</div>
				);
			},
		},
		...(isMobile
			? [
					{
						title: (
							<div className='flex justify-center'>
								<Link to={`/add-admin`}>
									<PlusOutlined className='flex items-center cursor-pointer text-[20px] text-black font-bold' />
								</Link>
							</div>
						),
						dataIndex: 'record',
						className: 'small-screen-row-header',
						render: (value, row) => {
							return (
								<div className='flex py-2 pb-0 justify-between country-and-paym-table-col'>
									<div className='flex-col w-full'>
										<div className='flex justify-between flex-row text-xs text-[#00142A66]'>
											{t<string>('name')}
											<div className='flex'>
												{row.role === 'ADMIN' && role === 'SUPER_USER' && (
													<Tooltip placement='top' title={t<string>('passwordChange')}>
														<Link to={`/change-password?userAdmin=${row.id}`}>
															<KeyOutlined className='px-1 flex items-center cursor-pointer  text-[14px] text-black font-bold' />
														</Link>
													</Tooltip>
												)}
												<Link to={`/add-admin?userAdmin=${row.id}`}>
													<EditFilled className='px-1 flex items-center cursor-pointer  text-[14px] text-black font-bold' />
												</Link>
												{isSpecialSuperUser && row.role === 'ADMIN' && (
													<StopOutlined
														className='px-1 flex items-baseline cursor-pointer  text-[14px] text-black font-bold'
														onClick={() => {
															setShowModal(true);
															setUser(row);
														}}
													/>
												)}
												<div className='text-[14px] text-[#00142A] flex items-center mb-1 font-medium'>{`${row.firstname} ${row.lastname}`}</div>
											</div>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('emailAddress')}</span>
											<span className='inline-block mx-1 text-[#000] text-[13px]'>{row.email}</span>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('countryCode')}</span>
											<span className='text-xs text-lightBlue mx-2'>{row.countryCode}</span>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('phoneNumber')}</span>
											<span className='inline-block mx-1 text-[#000] text-[13px]'>{row.phone}</span>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('role')}</span>
											<span className='text-xs text-lightBlue mx-2'>{`${row.role}`}</span>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('creationDate')}</span>
											<span className='text-xs text-lightBlue mx-2'>
												{dayjs(row.createdAt).format(DATE_TIME_FORMAT)}
											</span>
										</div>
										<div className='flex items-center justify-between h-6'>
											<span className='text-xs text-[#00142A66] '>{t<string>('status')}</span>
											<span
												className='text-xs text-lightBlue mx-2'
												style={{ color: row.userStatus === 'ACTIVE' ? 'green' : 'red' }}
											>
												{t(row.userStatus.toLocaleLowerCase())}
											</span>
										</div>
									</div>
								</div>
							);
						},
					} as TableColumnType<AdminUser>,
			  ]
			: []),
	];

	return columns;
};
